import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, LogOut, BellRing, CalendarDays, Mail, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const TABS = [
  { id: "sessions", label: "Sessions", Icon: CalendarDays },
  { id: "enquiries", label: "Enquiries", Icon: Mail },
  { id: "subscribers", label: "Reset Requests", Icon: Sparkles },
];

const fmtSlot = (iso) =>
  new Date(iso).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const Card = ({ children, testid }) => (
  <div className="border border-white/10 bg-white/[0.03] p-6" data-testid={testid}>
    {children}
  </div>
);

const Meta = ({ label, value }) => (
  <p className="text-sm text-[#B9B4AA] mt-1">
    <span className="text-[10px] uppercase tracking-[0.24em] text-goldsoft/80 mr-2">{label}</span>
    {value}
  </p>
);

export default function Admin() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [user, setUser] = useState(state?.user ?? null);
  const [authed, setAuthed] = useState(state?.user ? true : null);
  const [tab, setTab] = useState("sessions");
  const [data, setData] = useState({ upcoming: [], past: [], contacts: [], subscribers: [] });
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(false);
  const [reminderNote, setReminderNote] = useState("");

  useEffect(() => {
    if (state?.user) return;
    api
      .get("/auth/me")
      .then(({ data: me }) => {
        setUser(me);
        setAuthed(true);
      })
      .catch(() => {
        setAuthed(false);
        navigate("/admin/login", { replace: true });
      });
  }, [state, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, s] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
        api.get("/admin/subscribers"),
      ]);
      setData({
        upcoming: b.data.upcoming,
        past: b.data.past,
        contacts: c.data.contacts,
        subscribers: s.data.subscribers,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const signOut = async () => {
    await api.post("/auth/logout");
    navigate("/admin/login", { replace: true });
  };

  const runReminders = async () => {
    setReminding(true);
    setReminderNote("");
    try {
      const { data: r } = await api.post("/admin/reminders/run");
      setReminderNote(
        r.sent > 0
          ? `${r.sent} reminder${r.sent > 1 ? "s" : ""} sent.`
          : "Nothing due in the next 24 hours — reminders go out automatically.",
      );
    } catch {
      setReminderNote("Couldn't run reminders just now.");
    } finally {
      setReminding(false);
    }
  };

  if (authed === null) {
    return (
      <main className="min-h-screen bg-steel flex items-center justify-center" data-testid="admin-checking">
        <Loader2 size={22} className="text-goldsoft animate-spin" />
      </main>
    );
  }
  if (authed === false) return null;

  const counts = {
    sessions: data.upcoming.length,
    enquiries: data.contacts.length,
    subscribers: data.subscribers.length,
  };

  return (
    <main className="min-h-screen bg-steel" data-testid="admin-page">
      <header className="border-b border-white/10 bg-steel/95 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <img src="/images/logo-header-light.png" alt="T.A.L.K." className="h-9 w-auto" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-goldsoft">Bookings Inbox</span>
          </div>
          <div className="flex items-center gap-5">
            {user?.email && (
              <span className="hidden sm:inline text-xs text-[#B9B4AA]" data-testid="admin-user-email">
                {user.email}
              </span>
            )}
            <button
              onClick={runReminders}
              disabled={reminding}
              data-testid="admin-run-reminders-button"
              className="inline-flex items-center gap-2 border border-goldsoft/50 text-goldsoft text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 hover:bg-goldsoft hover:text-steel transition-colors duration-300 disabled:opacity-50"
            >
              <BellRing size={13} strokeWidth={1.6} /> {reminding ? "Sending…" : "Send due reminders"}
            </button>
            <button
              onClick={signOut}
              data-testid="admin-signout-button"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#B9B4AA] hover:text-offwhite transition-colors duration-300"
            >
              <LogOut size={13} strokeWidth={1.6} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {reminderNote && (
          <p className="mb-8 border border-goldsoft/30 bg-goldsoft/10 px-5 py-3 text-sm text-goldsoft" data-testid="admin-reminder-note">
            {reminderNote}
          </p>
        )}

        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4" data-testid="admin-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-[10px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                tab === id
                  ? "border-goldsoft bg-goldsoft text-steel"
                  : "border-white/10 text-[#B9B4AA] hover:border-goldsoft/60 hover:text-offwhite"
              }`}
            >
              <Icon size={13} strokeWidth={1.6} /> {label}
              <span className="opacity-70">({counts[id]})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center" data-testid="admin-loading">
            <Loader2 size={22} className="text-goldsoft animate-spin" />
          </div>
        ) : (
          <div className="mt-10">
            {tab === "sessions" && (
              <div data-testid="admin-sessions-panel">
                <h2 className="font-display text-offwhite text-2xl mb-6">Upcoming</h2>
                {data.upcoming.length === 0 ? (
                  <p className="text-sm text-[#B9B4AA]" data-testid="admin-no-upcoming">
                    No sessions booked yet. They'll appear here the moment payment clears.
                  </p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {data.upcoming.map((b) => (
                      <Card key={b.session_id} testid={`admin-booking-${b.session_id}`}>
                        <p className="font-display text-goldsoft text-xl">{fmtSlot(b.slot_start)} IST</p>
                        <p className="mt-3 font-display text-offwhite text-2xl">{b.name}</p>
                        <Meta label="Email" value={b.email} />
                        <Meta label="Package" value={b.package_label} />
                        <Meta label="Paid" value={`${b.currency.toUpperCase()} ${b.amount.toFixed(2)}`} />
                        <Meta label="Reminder" value={b.reminder_sent ? "Sent" : "Not yet due"} />
                        {b.note && (
                          <p className="mt-4 border-l border-goldsoft/50 pl-4 text-sm text-[#D5D1C9] leading-relaxed whitespace-pre-line">
                            {b.note}
                          </p>
                        )}
                      </Card>
                    ))}
                  </div>
                )}

                {data.past.length > 0 && (
                  <>
                    <h2 className="font-display text-offwhite text-2xl mt-14 mb-6">Already held</h2>
                    <div className="border-t border-white/10" data-testid="admin-past-list">
                      {data.past.map((b) => (
                        <div
                          key={b.session_id}
                          className="flex flex-wrap items-baseline justify-between gap-3 py-4 border-b border-white/10"
                        >
                          <span className="text-sm text-offwhite">{b.name}</span>
                          <span className="text-xs text-[#B9B4AA]">{fmtSlot(b.slot_start)} IST · {b.package_label}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === "enquiries" && (
              <div className="grid md:grid-cols-2 gap-4" data-testid="admin-enquiries-panel">
                {data.contacts.length === 0 ? (
                  <p className="text-sm text-[#B9B4AA]" data-testid="admin-no-enquiries">No enquiries yet.</p>
                ) : (
                  data.contacts.map((c) => (
                    <Card key={c.id} testid={`admin-contact-${c.id}`}>
                      <p className="text-[10px] uppercase tracking-[0.24em] text-goldsoft/80">{fmtDate(c.created_at)}</p>
                      <p className="mt-3 font-display text-offwhite text-2xl">{c.name}</p>
                      <Meta label="Email" value={c.email} />
                      <Meta label="Interested in" value={c.offering_label} />
                      <p className="mt-4 border-l border-goldsoft/50 pl-4 text-sm text-[#D5D1C9] leading-relaxed whitespace-pre-line">
                        {c.message}
                      </p>
                    </Card>
                  ))
                )}
              </div>
            )}

            {tab === "subscribers" && (
              <div data-testid="admin-subscribers-panel">
                {data.subscribers.length === 0 ? (
                  <p className="text-sm text-[#B9B4AA]" data-testid="admin-no-subscribers">No reset requests yet.</p>
                ) : (
                  <div className="border-t border-white/10">
                    {data.subscribers.map((s) => (
                      <div
                        key={s.id || s.email}
                        className="flex flex-wrap items-baseline justify-between gap-3 py-4 border-b border-white/10"
                        data-testid={`admin-subscriber-${s.email}`}
                      >
                        <span className="text-sm text-offwhite">{s.email}</span>
                        <span className="text-xs text-[#B9B4AA]">{fmtDate(s.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
